import { Calendar, momentLocalizer } from 'react-big-calendar'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import { CalendarPageQueryDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Filters from 'modules/core/components/Card/Filters'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import FILTER_OPTIONS from 'modules/core/components/Filters/filterOptions'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import { track } from 'modules/core/lib/metrics'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import presences from 'modules/attendance/store/collections/presences'
import Exports from 'modules/calendar/components/Exports'
import { useCalendarLeaves } from 'modules/calendar/hooks/useCalendarLeaves'
import type { RouteContextParams } from 'modules/calendar/lib/RouteContextParams'
import generateFetchData from 'modules/calendar/lib/generateFetchData'
import calendarMuncher from 'modules/calendar/lib/munchers'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'
import { useEngagementFeatureAvailability } from 'modules/engagement/hooks/useEngagementFeatureAvailability'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import events from 'modules/posts/store/collections/events'

import ExpandButton from '../components/Calendar/components/ExpandButton'
import '../components/Calendar/styles/calendarOverride.module.scss'
import useCalendarComponents from '../components/Calendar/useCalendarComponents'
import AddBulkTimeOffButton from '../components/CalendarRoot/AddBulkTimeOffButton'

const filterTodayLeaves = (calendarEventList: CalendarEvent[]): CalendarEvent[] => {
  const today = moment().startOf('day')
  return calendarEventList.filter((ev) => {
    if (ev.attributes.type !== 'leave') {
      return true
    }
    const start = ev.metadata('start_on')
    const finish = ev.metadata('finish_on')
    if (!finish) {
      return today.isSameOrAfter(start)
    }
    return today.isSameOrAfter(start) && today.isSameOrBefore(finish)
  })
}

const CalendarPage = () => {
  const { date, filtersForm, loading, range, onDateRangeChange, canSeeCalendar } =
    useOutletContext<RouteContextParams>()

  const { lastLeaveUpdatedInMilliseconds } = useCalendarLeaves()
  const localizer = momentLocalizer(moment)
  const filterByValue = filtersForm.get('filterBy').value
  const graphQLClient = useGraphQLClient()

  const { data } = useQuery(CalendarPageQueryDocument, {})
  const canSeeClockedIn = Boolean(data?.attendance._policies.companySeeClockedIn.can)
  const canEditBankHolidays = Boolean(data?.locations._policies.locationManage.can)
  const { canManageTimeoff, employee } = useCurrentSession()

  const canCalendarAccess =
    Boolean(data?.attendance._policies.companyCalendarAccess.can) && canSeeCalendar

  useUnsafeEffect(() => {
    track(graphQLClient, 'CalendarVisited')
  }, [graphQLClient])

  const munchedData =
    filterByValue === FILTER_OPTIONS.employees.away
      ? calendarMuncher(filterTodayLeaves(calendarEvents.toArray()))
      : calendarMuncher(calendarEvents.toArray())

  const calendarComponents = useCalendarComponents({
    munchedData,
    canEditBankHolidays,
  })

  const [, setLocalFilterByValue] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.calendarFilter)

  const communitiesAvailable = useEngagementFeatureAvailability(Features.communities_core)

  const { loading: changingData } = useAsyncData(() => {
    const fetchData = generateFetchData(date, filterByValue, true)
    setLocalFilterByValue(filterByValue)

    const ms = lastLeaveUpdatedInMilliseconds

    return Promise.all([
      communitiesAvailable &&
        events.fetch({
          data: {
            from: fetchData.from,
            to: fetchData.to,
            ms,
          },
        }),
      calendarEvents.fetch({
        data: fetchData,
      }),
      presences.fetch(),
    ])
  }, [date, filterByValue, lastLeaveUpdatedInMilliseconds])

  return (
    <ResponsivePage.Body
      showHeader={false}
      authorized={canCalendarAccess}
      loading={loading || changingData}
      error={null}
    >
      <Box gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween" height="s32">
          <Filters
            form={filtersForm}
            showFilterByTeam
            showFilterByAway
            showFilterByAwayThisMonth
            showFilterByTimeoffSupervisor={!!employee}
            showFilterByDirectManagedEmployee={!!employee}
            showFilterByManagedEmployee={!!employee}
            canSeeClockedIn={canSeeClockedIn}
          />

          <Box gap="s16" flexDirection="row">
            <DateNavigator
              regularPeriods={['month']}
              startDate={range[0]}
              endDate={range[1]}
              withRelativeDates={false}
              onDateRangeChange={(...newRange) => onDateRangeChange(newRange)}
            />
            <Exports />
            {canManageTimeoff && <AddBulkTimeOffButton />}
          </Box>
        </Box>
        <Calendar
          toolbar={false}
          localizer={localizer}
          views={['month']}
          selectable={false}
          date={date.toDate()}
          messages={{
            showMore: function renderExpandButton(): any {
              return <ExpandButton direction="up" />
            },
          }}
          events={munchedData.calendarEvents}
          components={calendarComponents as any}
        />
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(CalendarPage)
